import { useState } from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import OverdueBurndownCard from "./OverdueBurndownCard";
import WorkingSetBoard from "./WorkingSetBoard";
import WorkingSetBuilderModal from "./WorkingSetBuilderModal";
import RemoveWorkingSetDialog from "./RemoveWorkingSetDialog";
import { useWorkingSetActions } from "../hooks/useWorkingSetActions";
import SuggestedForToday from "./SuggestedForToday";
import { useAiFeatures } from "@flags";

const EmptyWorkingSets = ({ onAdd }) => (
  <div className="flex flex-col items-start gap-4 rounded-2xl border border-dashed border-zinc-300 p-8 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex flex-col gap-1">
      <p className="text-sm font-semibold text-zinc-800">Pin the accounts you work every day</p>
      <p className="max-w-xl text-sm text-zinc-500">
        A working set is a list of accounts that keeps itself up to date, like everyone over their credit limit or more
        than 60 days late. Each one shows here with a live count, and opens straight into Trade accounts.
      </p>
    </div>
    <button
      type="button"
      onClick={onAdd}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-zinc-900 px-4 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-zinc-700"
    >
      <PlusIcon aria-hidden="true" className="size-4" />
      New working set
    </button>
  </div>
);

const HomeTab = ({ workingSets, workingSetsLoading }) => {
  // null, or { slot } — the board slot a new set lands in (null slot = first free).
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const { duplicate, place } = useWorkingSetActions();
  const aiEnabled = useAiFeatures();

  return (
    <div className="flex flex-col gap-10">
      <OverdueBurndownCard workingSets={workingSets} />

      <section className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Working sets</h3>
          {workingSets.length > 0 && (
            <span className="text-xs text-zinc-400">Live counts · drag to rearrange · opens in Trade accounts</span>
          )}
        </div>
        {!workingSetsLoading && workingSets.length === 0 ? (
          <EmptyWorkingSets onAdd={() => setCreating({ slot: null })} />
        ) : (
          <WorkingSetBoard
            sets={workingSets}
            onPlace={(set, slot) => place(set, slot, workingSets)}
            onAdd={(slot) => setCreating({ slot })}
            onEdit={setEditing}
            onDuplicate={(set) => duplicate(set, workingSets)}
            onRemove={setRemoving}
          />
        )}
      </section>

      {aiEnabled && <SuggestedForToday />}

      <WorkingSetBuilderModal isOpen={!!creating} slot={creating?.slot ?? null} onClose={() => setCreating(null)} />
      <WorkingSetBuilderModal isOpen={!!editing} set={editing} onClose={() => setEditing(null)} />
      <RemoveWorkingSetDialog set={removing} onClose={() => setRemoving(null)} />
    </div>
  );
};

export default HomeTab;
